import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VoiceResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { VoiceIconBox, ProviderBadge, VoiceStatusPill } from './VoiceBadges';

/**
 * Linha de tabela de voz (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + colunas multi-linha (nome+descrição) + menu ••• (última col).
 */
export interface VoiceListItemProps {
  voice: VoiceResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete: (voiceId: string) => void;
  onToggleStatus: (voiceId: string, currentStatus: string) => void;
}

export const VoiceListItem: React.FC<VoiceListItemProps> = ({
  voice,
  selected = false,
  onToggleSelect,
  onDelete,
  onToggleStatus,
}) => {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const playSample = () => { void audioRef.current?.play(); };

  const actions: CardAction[] = [];
  if (voice.audioSampleUrl) {
    actions.push({ label: 'Ouvir amostra', icon: 'play', onClick: playSample });
  }
  actions.push(
    { label: 'Editar', icon: 'edit', onClick: () => navigate(`/automation/voices/${voice.id}/edit`) },
    {
      label: voice.status === 'active' ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(voice.id, voice.status),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(voice.id), variant: 'danger' },
  );

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`voice-row-${voice.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(voice.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`voice-check-${voice.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Voz: ícone + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <VoiceIconBox providerId={voice.providerId} className="w-9 h-9 rounded-lg" iconClassName="w-4 h-4" />
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {voice.name}
            </div>
            {voice.description && (
              <div className="text-xs truncate max-w-[280px]" style={{ color: 'var(--theme-muted-text)' }}>
                {voice.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Provider */}
      <td className="px-4 py-3">
        <ProviderBadge providerId={voice.providerId} />
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <VoiceStatusPill status={voice.status} />
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        {voice.audioSampleUrl && (
          <audio ref={audioRef} src={voice.audioSampleUrl} className="hidden" data-testid={`voice-audio-${voice.id}`} />
        )}
        <CardActionsMenu actions={actions} size="sm" data-testid={`voice-actions-${voice.id}`} />
      </td>
    </tr>
  );
};

VoiceListItem.displayName = 'VoiceListItem';
